<template>
  <div>
    <o-form :title="$t('core.template_file')"
            :url="'/api/system/template_files/' + $route.params.id + '?include=versions,associations'"
            class="ortic-form-horizontal"
            ref="form"
            @loaded="onLoaded">
      <template slot-scope="props">
        <b-row>
          <b-col>
            <o-input :label="$t('core.name')" v-model="props.item.name"/>
            <o-form-group :label="$t('core.format')">
              <b-form-select v-model="props.item.format" :options="formatOptions" class="w-100"></b-form-select>
            </o-form-group>
          </b-col>
          <b-col>
            <o-form-group :label="$t('core.active_version')">
              <div class="w-100" v-if="props.item.active_version">
                <span class="badge badge-success">v{{ props.item.active_version.version_number }}</span>
              </div>
              <div class="w-100" v-else>
                <span class="badge badge-warning">{{$t('core.no_active_version')}}</span>
              </div>
            </o-form-group>
          </b-col>
        </b-row>
      </template>

      <template slot="child" slot-scope="props">
        <b-tabs>
          <!-- Versions Tab -->
          <b-tab :title="$t('core.versions') + ' (' + (props.item.versions ? props.item.versions.length : 0) + ')'" active>
            <o-card>
              <div class="mb-3" v-if="$route.params.id !== 'new'">
                <b-btn variant="primary" @click="$refs.uploadModal.show()">
                  <fa icon="upload"/> {{$t('core.upload_version')}}
                </b-btn>
              </div>
              <div class="table-responsive">
                <table class="table">
                  <thead>
                    <tr>
                      <th>{{$t('core.version')}}</th>
                      <th>{{$t('core.status')}}</th>
                      <th>{{$t('core.file')}}</th>
                      <th>{{$t('core.uploaded_by')}}</th>
                      <th>{{$t('core.date')}}</th>
                      <th class="text-right">{{$t('core.actions')}}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(version, index) in props.item.versions" :key="version.id">
                      <td>v{{ version.version_number }}</td>
                      <td>
                        <span class="badge"
                              :class="{
                                'badge-warning': version.status === 'draft',
                                'badge-success': version.status === 'active',
                                'badge-secondary': version.status === 'obsolete'
                              }">
                          {{ version.status }}
                        </span>
                      </td>
                      <td>{{ version.file_name ? version.file_name.name : '' }}</td>
                      <td>{{ version.uploaded_by }}</td>
                      <td>{{ version.created_at | formatDate }}</td>
                      <td class="text-right text-nowrap">
                        <div class="btn-group btn-group-sm" role="group">
                          <b-btn variant="success" size="sm"
                                 v-if="version.status === 'draft'"
                                 @click="activateVersion(version.id)"
                                 :title="$t('core.activate')">
                            <fa icon="check"/>
                          </b-btn>
                          <b-btn variant="danger" size="sm"
                                 v-if="version.status === 'draft'"
                                 @click="deleteVersion(version.id)"
                                 :title="$t('core.delete')">
                            <fa icon="trash"/>
                          </b-btn>
                          <b-btn variant="primary" size="sm"
                                 @click="downloadVersion(version.id)"
                                 :title="$t('core.download')">
                            <fa icon="download"/>
                          </b-btn>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </o-card>
          </b-tab>

          <!-- Associations Tab -->
          <b-tab :title="$t('core.associations') + ' (' + (props.item.associations ? props.item.associations.length : 0) + ')'">
            <o-card>
              <div class="mb-3" v-if="$route.params.id !== 'new'">
                <b-btn variant="primary" @click="showAddAssociationModal">
                  <fa icon="plus"/> {{$t('core.add_association')}}
                </b-btn>
              </div>
              <div class="table-responsive">
                <table class="table">
                  <thead>
                    <tr>
                      <th>{{$t('core.contract_type')}}</th>
                      <th>{{$t('core.contract_year')}}</th>
                      <th>{{$t('core.contract_number')}}</th>
                      <th>{{$t('core.language')}}</th>
                      <th class="text-right">{{$t('core.actions')}}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(assoc, index) in props.item.associations" :key="assoc.id">
                      <td>{{ assoc.contract_type || $t('core.all') }}</td>
                      <td>{{ assoc.contract_year || $t('core.all') }}</td>
                      <td>{{ assoc.contract_no || $t('core.all') }}</td>
                      <td>{{ assoc.language || $t('core.all') }}</td>
                      <td class="text-right text-nowrap">
                        <div class="btn-group btn-group-sm" role="group">
                          <b-btn variant="secondary" size="sm"
                                 @click="deleteAssociation(assoc.id, index)">
                            <fa icon="times"/>
                          </b-btn>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </o-card>
          </b-tab>
        </b-tabs>
      </template>
    </o-form>

    <!-- Upload Version Modal -->
    <b-modal ref="uploadModal" :title="$t('core.upload_version')" @ok="uploadVersion">
      <o-form-group :label="$t('core.file')">
        <b-form-file
          v-model="uploadFile"
          accept=".docx"
          :placeholder="$t('core.choose_file')"
        ></b-form-file>
      </o-form-group>
    </b-modal>

    <!-- Add Association Modal -->
    <b-modal ref="addAssociationModal" :title="$t('core.add_association')" @ok="addAssociation">
      <o-form-group :label="$t('core.contract_type')">
        <o-value-select-control url="/api/sandoz/sales_lines"
                                text-field="sales_line"
                                value-field="sales_line"
                                v-model="newAssociation.contract_type">
        </o-value-select-control>
      </o-form-group>
      <o-input :label="$t('core.contract_year')" v-model="newAssociation.contract_year" type="number"/>
      <o-input :label="$t('core.contract_number')" v-model="newAssociation.contract_no"/>
      <o-form-group :label="$t('core.language')">
        <b-form-select v-model="newAssociation.language" :options="languageOptions" class="w-100"></b-form-select>
      </o-form-group>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  data () {
    return {
      formatOptions: [
        { value: 'PDF', text: 'PDF' },
        { value: 'DOCX', text: 'DOCX' }
      ],
      languageOptions: [
        { value: null, text: this.$t('core.all') },
        { value: 'DE', text: 'DE' },
        { value: 'FR', text: 'FR' },
        { value: 'IT', text: 'IT' },
        { value: 'EN', text: 'EN' }
      ],
      uploadFile: null,
      newAssociation: {
        contract_type: null,
        contract_year: null,
        contract_no: null,
        language: null
      }
    }
  },

  methods: {
    onLoaded () {
      // callback after form data is loaded
    },

    uploadVersion (bvModalEvt) {
      if (!this.uploadFile) {
        bvModalEvt.preventDefault()
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        const fileData = {
          file_name: {
            name: this.uploadFile.name,
            data: e.target.result
          }
        }

        this.$root.$loading.start()
        axios.post(`/api/system/template_files/${this.$route.params.id}/versions`, fileData)
          .then(() => {
            this.uploadFile = null
            this.$nextTick(() => {
              this.$refs.form.reload()
            })
            this.$root.$loading.finish()
          })
          .catch((error) => {
            this.$root.$loading.finish()
            console.error(error)
          })
      }
      reader.readAsDataURL(this.uploadFile)
    },

    activateVersion (versionId) {
      this.$root.$loading.start()
      axios.post(`/api/system/template_files/${this.$route.params.id}/versions/${versionId}/activate`)
        .then(() => {
          this.$refs.form.reload()
          this.$root.$loading.finish()
        })
        .catch((error) => {
          this.$root.$loading.finish()
          console.error(error)
        })
    },

    deleteVersion (versionId) {
      this.$root.$loading.start()
      axios.delete(`/api/system/template_files/${this.$route.params.id}/versions/${versionId}`)
        .then(() => {
          this.$refs.form.reload()
          this.$root.$loading.finish()
        })
        .catch((error) => {
          this.$root.$loading.finish()
          console.error(error)
        })
    },

    downloadVersion (versionId) {
      this.$root.$loading.start()
      axios.get(`/api/system/template_files/${this.$route.params.id}/versions/${versionId}/download`, {
        responseType: 'arraybuffer'
      })
        .then((response) => {
          const contentDisposition = response.headers['content-disposition']
          let fileName = 'template.docx'
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^";\s]+)"?/)
            if (match) {
              fileName = match[1]
            }
          }
          require('downloadjs')(response.data, fileName)
          this.$root.$loading.finish()
        })
        .catch((error) => {
          this.$root.$loading.finish()
          console.error(error)
        })
    },

    showAddAssociationModal () {
      this.newAssociation = {
        contract_type: null,
        contract_year: null,
        contract_no: null,
        language: null
      }
      this.$refs.addAssociationModal.show()
    },

    addAssociation () {
      this.$root.$loading.start()
      axios.post('/api/system/template_associations/new', {
        template_file_id: parseInt(this.$route.params.id),
        contract_type: this.newAssociation.contract_type || null,
        contract_year: this.newAssociation.contract_year ? parseInt(this.newAssociation.contract_year) : null,
        contract_no: this.newAssociation.contract_no || null,
        language: this.newAssociation.language || null
      })
        .then(() => {
          this.$refs.form.reload()
          this.$root.$loading.finish()
        })
        .catch((error) => {
          this.$root.$loading.finish()
          console.error(error)
        })
    },

    deleteAssociation (associationId, index) {
      this.$root.$loading.start()
      axios.delete(`/api/system/template_associations/${associationId}`)
        .then(() => {
          this.$refs.form.reload()
          this.$root.$loading.finish()
        })
        .catch((error) => {
          this.$root.$loading.finish()
          console.error(error)
        })
    }
  }
}
</script>
